import { toaster } from 'components/ui/toaster';
import { toastContent, toastDuration } from 'config/toast';
import { MiscStatusType } from 'types/StatusTypes';
import { getUserFacingError, isTranslatorRelatedError } from 'utils/getUserFacingError';
import { captureClassifiedException } from 'utils/captureClassifiedException';

const GLOBAL_RUNTIME_ERROR_TOAST_ID = 'global-runtime-error';
const TRANSLATOR_WARNING_TOAST_ID = 'translator-active-warning';

function showGlobalErrorFeedback(error: unknown): void {
  if (isTranslatorRelatedError(error)) {
    if (toaster.isVisible(TRANSLATOR_WARNING_TOAST_ID)) {
      return;
    }

    const translatorToast = toastContent.misc[MiscStatusType.TRANSLATOR_ACTIVE];
    toaster.create({
      id: TRANSLATOR_WARNING_TOAST_ID,
      type: 'warning',
      title: translatorToast.title,
      description: translatorToast.description,
      duration: toastDuration,
      closable: true,
    });
    return;
  }

  if (toaster.isVisible(GLOBAL_RUNTIME_ERROR_TOAST_ID)) {
    return;
  }

  const { title, description } = getUserFacingError(error);
  toaster.create({
    id: GLOBAL_RUNTIME_ERROR_TOAST_ID,
    type: 'error',
    title,
    description,
    duration: toastDuration,
    closable: true,
  });
}

export function setupGlobalErrorHandlers(): void {
  window.addEventListener('error', (event) => {
    // Ignore failed image/script loads — only surface JavaScript runtime errors.
    if (!event.error && event.target && event.target !== window) {
      return;
    }

    const error = event.error ?? event.message;

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[Global runtime error]', error);
    }

    captureClassifiedException(error, { source: 'global-runtime', layer: 'runtime' });
    showGlobalErrorFeedback(error);
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[Unhandled promise rejection]', event.reason);
    }

    captureClassifiedException(event.reason, {
      source: 'global-unhandledrejection',
      layer: 'runtime',
    });
    showGlobalErrorFeedback(event.reason);
  });
}
